import { RowContextMenuItemParams } from '@/components/table/components/RowContextMenu.tsx';
import { RowWithId } from '@/components/table/types/RowWithId.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import useCancelMessage from '@/features/dashboard/messages/hooks/useCancelMessage.ts';
import useDeleteMessage from '@/features/dashboard/messages/hooks/useDeleteMessage.ts';
import useRetryMessage from '@/features/dashboard/messages/hooks/useRetryMessage.ts';
import {
  canCancelMessageRow,
  canRetryMessageRow,
} from '@/features/dashboard/messages/types/MessageStatusDto.utils.ts';

/**
 * Menu wiersza każdej listy wiadomości — firmy, wyjazdu, rezerwacji, klienta i uczestnika.
 * Jedno miejsce, bo wiadomość to ta sama wiadomość niezależnie od tego, którą listą się do niej doszło.
 *
 * `tourId` steruje wyłącznie tym, czyj panel spraw gubi „Niewysłane wiadomości” od razu po ponowieniu
 * i archiwizacji — lista, która wyjazdu nie zna (firmowa), go nie podaje. Anulowanie panelu nie rusza:
 * anulować da się tylko wiadomość zaplanowaną, a ta do sprawy nigdy nie weszła.
 */
const useMessageRowActions = (
  tourId?: string,
): RowContextMenuItemParams<RowWithId>[] => {
  const cancelMessage = useCancelMessage();
  const retryMessage = useRetryMessage(tourId);
  const deleteMessage = useDeleteMessage(tourId);
  const { confirmDeleteWithCallback, confirmWithCallback } =
    confirmDialogUseHook();

  return [
    {
      label: 'Anuluj',
      action: (row) => {
        confirmWithCallback({
          title: 'Anuluj wiadomość',
          description: 'Czy na pewno chcesz anulować tę wiadomość?',
          confirmText: 'Anuluj wiadomość',
          loadingText: 'Anulowanie...',
          onConfirm: () => cancelMessage.mutateAsync(row.id),
        });
      },
      visible: canCancelMessageRow,
    },
    {
      label: 'Ponów wysyłkę',
      action: (row) => {
        confirmWithCallback({
          title: 'Ponów wysyłkę',
          description:
            'Wiadomość wróci do kolejki i wyjdzie od razu, z pełną pulą prób. Upewnij się, że przyczyna błędu zniknęła.',
          confirmText: 'Ponów wysyłkę',
          loadingText: 'Ponawianie...',
          onConfirm: () => retryMessage.mutateAsync(row.id),
        });
      },
      visible: canRetryMessageRow,
    },
    {
      label: 'Archiwizuj',
      variant: 'destructive',
      action: (row) => {
        confirmDeleteWithCallback({
          title: 'Archiwizuj wiadomość',
          description: 'Czy na pewno chcesz zarchiwizować wiadomość?',
          confirmText: 'Archiwizuj',
          loadingText: 'Archiwizowanie...',
          onConfirm: () => deleteMessage.mutateAsync(row.id),
        });
      },
    },
  ];
};

export default useMessageRowActions;
